import type { Translation } from '@si/utils';

const tr = (
  en: string,
  nb: string,
  de: string,
  sv: string,
  fi: string,
  cz: string
): Translation => ({ en, nb, de, sv, fi, cz });

export const learningContent = {
  realtimeOverviewDescription: tr(
    'Choose Realtime for the latest readings, or a time period to explore historical data. Select Period summary to see a summarized view of the entire selected period.',
    'Velg Sanntid for de nyeste målingene, eller en tidsperiode for historiske data. Velg Periodesammendrag for å se en oppsummering av hele den valgte perioden.',
    'Wählen Sie Echtzeit für aktuelle Messwerte oder einen Zeitraum für historische Daten. Wählen Sie die Zeitraumzusammenfassung, um eine zusammengefasste Ansicht des gesamten gewählten Zeitraums zu sehen.',
    'Välj Realtid för de senaste mätvärdena, eller en tidsperiod för historiska data. Välj Periodsammanfattning för att se en sammanfattning av hela den valda perioden.',
    'Valitse Reaaliaika uusimmille lukemille tai ajanjakso historiatietojen tutkimiseen. Valitse Jaksoyhteenveto nähdäksesi yhteenvedon koko valitusta ajanjaksosta.',
    'Vyberte Reálný čas pro nejnovější měření nebo období pro historická data. Vyberte Souhrn období a zobrazte souhrnný pohled na celé vybrané období.'
  ),
  realtimeOverviewTitle: tr(
    'Choose a time period',
    'Velg tidsperiode',
    'Zeitraum wählen',
    'Välj tidsperiod',
    'Valitse ajanjakso',
    'Vyberte období'
  ),
  settingsTabDescription: tr(
    'View sensor thresholds and see who has access to the project.',
    'Se sensorgrenser og hvem som har tilgang til prosjektet.',
    'Sehen Sie Sensorschwellenwerte und wer Zugriff auf das Projekt hat.',
    'Se sensorgränser och vilka som har tillgång till projektet.',
    'Tarkastele anturien raja-arvoja ja projektin käyttöoikeuksia.',
    'Prohlédněte si prahové hodnoty senzorů a kdo má přístup k projektu.'
  ),
  documentationTabDescription: tr(
    'Find reports, installation photos, plans, and guides.',
    'Finn rapporter, installasjonsbilder, planer og veiledninger.',
    'Finden Sie Berichte, Installationsfotos, Pläne und Anleitungen.',
    'Hitta rapporter, installationsbilder, planer och guider.',
    'Löydä raportit, asennuskuvat, suunnitelmat ja oppaat.',
    'Najděte zprávy, instalační fotografie, plány a návody.'
  ),
  installationTabDescription: tr(
    'Find sensors and gateways on maps and view their details.',
    'Finn sensorer og gatewayer på kart og se detaljer.',
    'Finden Sie Sensoren und Gateways auf Karten und sehen Sie deren Details.',
    'Hitta sensorer och gateways på kartor och se deras detaljer.',
    'Etsi anturit ja yhdyskäytävät kartalta ja tarkastele niiden tietoja.',
    'Najděte senzory a brány na mapách a prohlédněte si jejich podrobnosti.'
  ),
  dashboardTabDescription: tr(
    'Get an overview of project status, sensor alerts, and recent changes.',
    'Få oversikt over prosjektstatus, sensorvarsler og siste endringer.',
    'Erhalten Sie einen Überblick über Projektstatus, Sensorwarnungen und aktuelle Änderungen.',
    'Få en överblick över projektstatus, sensorlarm och senaste förändringar.',
    'Tarkastele projektin tilaa, anturihälytyksiä ja viimeaikaisia muutoksia.',
    'Získejte přehled o stavu projektu, upozorněních senzorů a posledních změnách.'
  ),
  dataTabDescription: tr(
    'Explore installation maps, latest readings and historical analysis.',
    'Utforsk installasjonskart, de nyeste målingene og historisk analyse.',
    'Erkunden Sie Installationskarten, die neuesten Messwerte und historische Analysen.',
    'Utforska installationskartor, de senaste mätvärdena och historisk analys.',
    'Tutustu asennuskarttoihin, uusimpiin lukemiin ja historia-analyysiin.',
    'Prozkoumejte instalační mapy, nejnovější měření a historickou analýzu.'
  ),
  learningCenter: tr(
    'Learning center',
    'Læringssenter',
    'Lerncenter',
    'Utbildningscenter',
    'Oppimiskeskus',
    'Vzdělávací centrum'
  ),
  introduction: tr(
    'Find help with the features of the into® Control System here.',
    'Her finner du hjelp til funksjonene i into® Control System.',
    'Hier finden Sie Hilfe zu den Funktionen des into® Control System.',
    'Här hittar du hjälp med funktionerna i into® Control System.',
    'Täältä löydät ohjeita into® Control Systemin toimintoihin.',
    'Zde najdete nápovědu k funkcím into® Control System.'
  ),
  closeLearningCenter: tr(
    'Close learning center',
    'Lukk læringssenter',
    'Lerncenter schließen',
    'Stäng utbildningscenter',
    'Sulje oppimiskeskus',
    'Zavřít vzdělávací centrum'
  ),
  dataOverviewTitle: tr('Data', 'Data', 'Data', 'Data', 'Data', 'Data'),
  mappingTitle: tr(
    'Sensor map',
    'Sensorkart',
    'Sensorkarte',
    'Sensorkarta',
    'Anturikartta',
    'Mapa senzorů'
  ),
  chooseProject: tr(
    'Choose project',
    'Velg prosjekt',
    'Projekt wählen',
    'Välj projekt',
    'Valitse projekti',
    'Vybrat projekt'
  ),
  tourStep: tr(
    'Step {{current}} of {{total}}',
    'Trinn {{current}} av {{total}}',
    'Schritt {{current}} von {{total}}',
    'Steg {{current}} av {{total}}',
    'Vaihe {{current}}/{{total}}',
    'Krok {{current}} z {{total}}'
  ),
  previous: tr('Previous', 'Forrige', 'Zurück', 'Föregående', 'Edellinen', 'Předchozí'),
  next: tr('Next', 'Neste', 'Weiter', 'Nästa', 'Seuraava', 'Další'),
  finish: tr('Finish', 'Fullfør', 'Fertig', 'Slutför', 'Valmis', 'Dokončit'),
  startFullTour: tr(
    'Start walkthrough',
    'Start gjennomgang',
    'Rundgang starten',
    'Starta genomgång',
    'Aloita opastus',
    'Spustit průvodce'
  ),
  filtersTitle: tr(
    'Set the time period',
    'Velg tidsperiode',
    'Zeitraum festlegen',
    'Välj tidsperiod',
    'Valitse ajanjakso',
    'Nastavte časové období'
  ),
  periodDescription: tr(
    'Realtime shows the latest sensor readings for one installation. Choose a preset period or a custom date range to open historical analysis and charts. Presets load immediately; apply a custom range in the date picker. Period summary is available for historical data.',
    'Sanntid viser de nyeste sensormålingene for én installasjon. Velg en forhåndsdefinert periode eller et egendefinert datointervall for historisk analyse og grafer. Forhåndsdefinerte perioder lastes direkte; bruk et egendefinert intervall i datovelgeren. Periodesammendrag er tilgjengelig for historiske data.',
    'Echtzeit zeigt die neuesten Sensorwerte einer Installation. Wählen Sie einen vordefinierten oder eigenen Zeitraum für historische Analysen und Diagramme. Vordefinierte Zeiträume laden sofort; eigene Zeiträume übernehmen Sie in der Datumsauswahl. Die Zeitraumzusammenfassung ist für historische Daten verfügbar.',
    'Realtid visar de senaste sensorvärdena för en installation. Välj en förinställd period eller ett eget datumintervall för historisk analys och diagram. Förinställda perioder laddas direkt; tillämpa egna intervall i datumväljaren. Periodsammanfattning är tillgänglig för historiska data.',
    'Reaaliaika näyttää yhden asennuksen uusimmat anturilukemat. Valitse valmis tai oma ajanjakso avataksesi historia-analyysin ja kaaviot. Valmiit jaksot latautuvat heti; ota oma jakso käyttöön päivämäärävalitsimessa. Jaksoyhteenveto on saatavilla historiatiedoille.',
    'Reálný čas ukazuje nejnovější hodnoty senzorů jedné instalace. Pro historickou analýzu a grafy vyberte přednastavené nebo vlastní období. Přednastavená období se načtou ihned; vlastní rozsah potvrďte ve výběru data. Souhrn období je dostupný pro historická data.'
  ),
  installationTitle: tr(
    'Select installation or all data combined',
    'Velg installasjon eller alle data samlet',
    'Installation oder alle Daten kombiniert wählen',
    'Välj installation eller alla data kombinerade',
    'Valitse asennus tai kaikki tiedot yhdistettynä',
    'Vyberte instalaci nebo všechna data dohromady'
  ),
  installationDescription: tr(
    'Choose an installation to see its sensor map, or All data combined to compare data across installations.',
    'Velg en installasjon for å se sensorkartet, eller Alle data samlet for å sammenligne data på tvers av installasjoner.',
    'Wählen Sie eine Installation für die Sensorkarte oder Alle Daten kombiniert, um Daten zwischen Installationen zu vergleichen.',
    'Välj en installation för att se sensorkartan, eller Alla data kombinerade för att jämföra data mellan installationer.',
    'Valitse asennus nähdäksesi anturikartan tai Kaikki tiedot yhdistettynä vertaillaksesi asennusten tietoja.',
    'Vyberte instalaci pro mapu senzorů nebo Všechna data dohromady pro porovnání dat mezi instalacemi.'
  ),
  sensorGroupTitle: tr(
    'Choose sensor type and position',
    'Velg sensortype og plassering',
    'Sensortyp und Position wählen',
    'Välj sensortyp och placering',
    'Valitse anturityyppi ja sijainti',
    'Vyberte typ a umístění senzoru'
  ),
  combinedGroupsDescription: tr(
    'Select sensor types and positions to filter the map and charts. Available analyses depend on your selection.',
    'Velg sensortyper og plasseringer for å filtrere kart og grafer. Tilgjengelige analyser avhenger av valget ditt.',
    'Wählen Sie Sensortypen und Positionen, um Karte und Diagramme zu filtern. Verfügbare Analysen hängen von Ihrer Auswahl ab.',
    'Välj sensortyper och placeringar för att filtrera karta och diagram. Tillgängliga analyser beror på ditt val.',
    'Valitse anturityypit ja sijainnit suodattaaksesi karttaa ja kaavioita. Saatavilla olevat analyysit riippuvat valinnastasi.',
    'Vyberte typy a polohy senzorů pro filtrování mapy a grafů. Dostupné analýzy závisí na vašem výběru.'
  ),
  sensorMetricTitle: tr(
    'Choose an analysis',
    'Velg analyse',
    'Analyse wählen',
    'Välj analys',
    'Valitse analyysi',
    'Vyberte analýzu'
  ),
  sensorMetricDescription: tr(
    'Choose what the map and charts show: moisture, wood moisture, downtime, temperature, or relative humidity. Available analyses depend on your sensors.',
    'Velg hva kart og grafer viser: fukt, trefukt, nedetid, temperatur eller relativ luftfuktighet. Tilgjengelige analyser avhenger av sensorene dine.',
    'Wählen Sie die Anzeige für Karte und Diagramme: Feuchte, Holzfeuchte, Ausfallzeit, Temperatur oder relative Luftfeuchtigkeit. Verfügbare Analysen hängen von Ihren Sensoren ab.',
    'Välj vad karta och diagram visar: fukt, träfukt, driftstopp, temperatur eller relativ luftfuktighet. Tillgängliga analyser beror på dina sensorer.',
    'Valitse kartan ja kaavioiden tiedot: kosteus, puukosteus, käyttökatkot, lämpötila tai suhteellinen kosteus. Saatavilla olevat analyysit riippuvat antureistasi.',
    'Vyberte, co zobrazí mapa a grafy: vlhkost, vlhkost dřeva, výpadky, teplota nebo relativní vlhkost. Dostupné analýzy závisí na vašich senzorech.'
  ),
  combinedOverviewDescription: tr(
    'View latest readings, maps, and historical charts for your installations.',
    'Se de nyeste målingene, kart og historiske grafer for installasjonene dine.',
    'Sehen Sie die neuesten Messwerte, Karten und historischen Diagramme Ihrer Installationen.',
    'Se de senaste mätvärdena, kartor och historiska diagram för dina installationer.',
    'Tarkastele asennustesi uusimpia lukemia, karttoja ja historiakaavioita.',
    'Prohlížejte nejnovější měření, mapy a historické grafy vašich instalací.'
  ),
  chartInspectionTitle: tr(
    'Explore the chart',
    'Utforsk grafen',
    'Diagramm erkunden',
    'Utforska diagrammet',
    'Tutki kaaviota',
    'Prozkoumejte graf'
  ),
  chartInspectionDescription: tr(
    'Use the legend to focus on groups or individual sensors, and hover over the chart to inspect values.',
    'Bruk tegnforklaringen for å fokusere på grupper eller enkeltsensorer, og hold pekeren over grafen for å se verdier.',
    'Nutzen Sie die Legende für Gruppen oder einzelne Sensoren und bewegen Sie den Mauszeiger über das Diagramm, um Werte zu sehen.',
    'Använd teckenförklaringen för grupper eller enskilda sensorer och håll pekaren över diagrammet för att se värden.',
    'Rajaa ryhmiä tai antureita selitteellä ja tarkastele arvoja viemällä osoitin kaavion päälle.',
    'Legendou vyberte skupiny nebo jednotlivé senzory a najetím na graf zobrazte hodnoty.'
  ),
  mapOverviewDescription: tr(
    'Hover over a sensor to see its values. With a historical period selected, use Play or the timeline to explore changes over time.',
    'Hold pekeren over en sensor for å se verdiene. Når en historisk periode er valgt, bruk Spill av eller tidslinjen for å utforske endringer over tid.',
    'Bewegen Sie den Mauszeiger über einen Sensor, um seine Werte zu sehen. Bei einem historischen Zeitraum können Sie mit Wiedergabe oder der Zeitachse Änderungen im Zeitverlauf erkunden.',
    'Håll pekaren över en sensor för att se dess värden. När en historisk period är vald kan du använda Spela upp eller tidslinjen för att utforska förändringar över tid.',
    'Vie osoitin anturin päälle nähdäksesi sen arvot. Kun historiallinen ajanjakso on valittu, tutki muutoksia toistolla tai aikajanalla.',
    'Najetím na senzor zobrazíte jeho hodnoty. Po výběru historického období použijte přehrávání nebo časovou osu k prozkoumání změn v čase.'
  ),
  closeGuide: tr(
    'Close guide',
    'Lukk veiledningen',
    'Anleitung schließen',
    'Stäng guiden',
    'Sulje opas',
    'Zavřít průvodce'
  ),
} as const;
